import { Add, Close } from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Button } from '@theguarantors/tg-package-material-ui';

import type { ApplicantsPresentation, SectionConfig } from '../../lease-details.config';
import { collectFieldDefaults } from '../../lease-details.defaults';
import { FieldsRenderer } from '../fields-renderer';
import { SectionShell } from '../section-shell';

// The only array-editor section in the engine; backed by RHF `useFieldArray`.

export type ApplicantsSectionProps = {
  config: SectionConfig & ApplicantsPresentation;
};

const APPLICANTS_FIELD = 'applicants';

export const ApplicantsSection = ({ config }: ApplicantsSectionProps) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: APPLICANTS_FIELD });

  const canAdd = fields.length < config.max;
  const canRemove = fields.length > config.min;

  const handleAdd = () => append(collectFieldDefaults(config.fields));

  return (
    <SectionShell title={config.title} subtitle={config.subtitle} slots={config.slots}>
      <Stack direction="column" spacing={2}>
        {fields.map((field, index) => {
          const isPrimary = index === 0;
          const isLocked = isPrimary && config.primaryLocked;
          const showRemove = !isPrimary && canRemove;
          return (
            <Stack key={field.id} direction="row" spacing={1} alignItems="flex-start">
              <Stack direction="column" spacing={1} sx={{ flexGrow: 1 }}>
                <FieldsRenderer
                  fields={config.fields}
                  sectionKey="applicants"
                  arrayIndex={index}
                  inheritedDisabled={isLocked}
                />
              </Stack>
              {showRemove && (
                <IconButton
                  aria-label={`remove applicant ${index + 1}`}
                  id={`applicants-${index}-remove`}
                  onClick={() => remove(index)}
                  size="small"
                >
                  <Close />
                </IconButton>
              )}
            </Stack>
          );
        })}
        {canAdd && (
          <Button
            id="applicants-add"
            startIcon={<Add />}
            onClick={handleAdd}
            variant="text"
            size="medium"
            color="primary"
          >
            Add applicant
          </Button>
        )}
      </Stack>
    </SectionShell>
  );
};
